import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
} from 'chart.js';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const DNAbsenceReasons = ({
  issues,
  colorMapping,
  dataKey = 'reason',
  title = 'Raison',
  limit = 0,
  withOthers = false,
  onItemSelect = null,
  obfuscate
}) => {
  const isDarkMode = document.documentElement.classList.contains('dark');
  const chartRef = useRef(null);
  const [chartType, setChartType] = useState('bar'); // 'doughnut' ou 'bar'

  // Définir la fonction getColorForIndex
  const getColorForIndex = (index, itemKey) => {
    // Vérifier si colorMapping est un objet de palettes ou un mapping direct
    if (colorMapping && colorMapping.backgrounds) {
      // C'est une palette de couleurs
      const colorIndex = index % colorMapping.backgrounds.length;
      return {
        background: colorMapping.backgrounds[colorIndex],
        border: colorMapping.borders[colorIndex],
        backgroundWithOpacity: colorMapping.backgroundsWithOpacity?.[colorIndex] || `${colorMapping.backgrounds[colorIndex]}40`
      };
    } else if (colorMapping && typeof colorMapping === 'object') {
      // C'est un mapping direct
      return colorMapping[itemKey] || {
        background: '#888888',
        border: '#666666',
        backgroundWithOpacity: '#88888840'
      };
    }

    // Fallback sur des couleurs grises par défaut
    return {
      background: '#888888',
      border: '#666666',
      backgroundWithOpacity: '#88888840'
    };
  };

  // Calculer les stats
  const itemStats = useMemo(() => {
    if (!issues.length) return [];

    const stats = issues.reduce((acc, issue) => {
      const key = issue[dataKey] || 'Non catégorisé';
      if (!acc[key]) {
        acc[key] = { count: 0, reason: key };
      }
      acc[key].count++;
      return acc;
    }, {});

    // Convertir en tableau, calculer les pourcentages et trier
    let result = Object.values(stats)
      .map(stat => ({
        ...stat,
        percentage: (stat.count / issues.length) * 100
      }))
      .sort((a, b) => b.count - a.count);

    // Appliquer la limite si nécessaire
    if (limit > 0 && result.length > limit) {
      // Garder les "limit" premiers éléments et regrouper le reste dans "Autres"
      const topItems = result.slice(0, limit);
      const otherItems = result.slice(limit);

      // Calculer le total pour "Autres"
      const otherCount = otherItems.reduce((sum, item) => sum + item.count, 0);
      const otherPercentage = otherItems.reduce((sum, item) => sum + item.percentage, 0);

      // Ajouter l'entrée "Autres" si nécessaire
      if (withOthers && otherCount > 0) {
        topItems.push({
          reason: `Autres (${otherItems.length})`,
          count: otherCount,
          percentage: otherPercentage
        });
      }

      result = topItems;
    }

    return result;
  }, [issues, dataKey, limit, withOthers]);

  const handleElementClick = (reason) => {
    if (onItemSelect && typeof onItemSelect === 'function') {
      const selectedStat = itemStats.find(stat => stat.reason === reason);
      onItemSelect(selectedStat, reason);
    }
  };

  // Adapter les données pour le type de graphique sélectionné
  const chartData = useMemo(() => {
    if (!itemStats.length) return null;

    if (chartType === 'doughnut') {
      return {
        labels: itemStats.map(stat => obfuscate ? `${stat.reason.slice(0, 2)}${'*'.repeat(Math.max(0, stat.reason.length - 2))}` : stat.reason),
        datasets: [{
          data: itemStats.map(stat => stat.count),
          backgroundColor: itemStats.map((stat, index) =>
            getColorForIndex(index, stat.reason).background
          ),
          borderColor: itemStats.map((stat, index) =>
            getColorForIndex(index, stat.reason).border
          ),
          borderWidth: 1
        }]
      };
    } else {
      return {
        labels: itemStats.map(stat => obfuscate ? `${stat.reason.slice(0, 2)}${'*'.repeat(Math.max(0, stat.reason.length - 2))}` : stat.reason),
        datasets: [{
          label: `Nombre de ${title.toLowerCase()}`,
          data: itemStats.map(stat => stat.count),
          backgroundColor: itemStats.map((stat, index) =>
            getColorForIndex(index, stat.reason).background
          ),
          borderColor: itemStats.map((stat, index) =>
            getColorForIndex(index, stat.reason).border
          ),
          borderWidth: 1
        }]
      };
    }
  }, [itemStats, chartType, title]);

  // Adapter les options selon le type de graphique
  const chartOptions = useMemo(() => {
    // Options de base communes
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: chartType === 'doughnut' ? 'right' : 'top',
          onClick: (evt, legendItem) => {
            // Trouver la raison correspondante à partir du texte de l'étiquette
            let reason;
            if (chartType === 'doughnut') {
              reason = itemStats.find((stat, i) => i === legendItem.index)?.reason;
            } else {
              const index = legendItem.index === undefined ? 0 : legendItem.index;
              reason = chartData.labels[index];
            }
            if (reason) {
              handleElementClick(reason);
            }
          },
          labels: {
            color: isDarkMode ? '#E5E7EB' : '#374151',
            usePointStyle: true,
            pointStyle: 'circle',
            padding: 20,
            font: {
              size: chartType === 'doughnut' ? 12 : 11
            },
            generateLabels: (chart) => {
              if (chartType === 'doughnut') {
                return itemStats.map((stat, i) => {
                  return {
                    text: `${stat.reason} (${stat.count} - ${stat.percentage.toFixed(1)}%)`,
                    fillStyle: getColorForIndex(i, stat.reason).background,
                    fontColor: isDarkMode ? '#E5E7EB' : '#374151',
                    strokeStyle: 'transparent',
                    pointStyle: 'circle',
                    index: i
                  };
                });
              } else {
                // Pour le bar chart, n'afficher aucune légende
                return [];
              }
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              if (chartType === 'doughnut') {
                const stat = itemStats[context.dataIndex];
                return ` ${stat.count} (${stat.percentage.toFixed(1)}%)`;
              } else {
                const value = context.raw;
                const total = itemStats.reduce((sum, stat) => sum + stat.count, 0);
                const percentage = (value / total) * 100;
                return ` ${value} (${percentage.toFixed(1)}%)`;
              }
            },
            title: (context) => {
              return `${title}: ${context[0].label}`;
            }
          }
        }
      },
      onClick: (evt, elements) => {
        if (elements.length > 0) {
          let reason;
          if (chartType === 'doughnut') {
            const index = elements[0].index;
            reason = chartData.labels[index];
          } else {
            const index = elements[0].index;
            reason = chartData.labels[index];
          }
          if (reason) {
            handleElementClick(reason);
          }
        }
      }
    };

    // Options spécifiques au doughnut
    if (chartType === 'doughnut') {
      return {
        ...baseOptions,
        cutout: '60%'
      };
    }
    // Options spécifiques au bar chart
    else {
      return {
        ...baseOptions,
        indexAxis: 'y', // Pour un bar chart horizontal
        scales: {
          x: {
            beginAtZero: true,
            grid: {
              color: isDarkMode ? '#374151' : '#E5E7EB',
            },
            ticks: {
              color: isDarkMode ? '#E5E7EB' : '#374151',
            }
          },
          y: {
            grid: {
              display: false
            },
            ticks: {
              color: isDarkMode ? '#E5E7EB' : '#374151',
            }
          }
        },
        plugins: {
          ...baseOptions.plugins,
          legend: {
            display: false // Cacher la légende pour le bar chart
          }
        }
      };
    }
  }, [chartType, itemStats, isDarkMode, chartData?.labels, title, onItemSelect]);

  // Icônes pour les boutons de switch
  const DoughnutIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path fillRule="evenodd" d="M2.25 13.5a8.25 8.25 0 018.25-8.25.75.75 0 01.75.75v6.75H18a.75.75 0 01.75.75 8.25 8.25 0 01-16.5 0z" clipRule="evenodd" />
      <path fillRule="evenodd" d="M12.75 3a.75.75 0 01.75-.75 8.25 8.25 0 018.25 8.25.75.75 0 01-.75.75h-7.5a.75.75 0 01-.75-.75V3z" clipRule="evenodd" />
    </svg>
  );

  const BarChartIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M18.375 2.25c-1.035 0-1.875.84-1.875 1.875v15.75c0 1.035.84 1.875 1.875 1.875h.75c1.035 0 1.875-.84 1.875-1.875V4.125c0-1.036-.84-1.875-1.875-1.875h-.75zM9.75 8.625c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-.75a1.875 1.875 0 01-1.875-1.875V8.625zM3 13.125c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v6.75c0 1.035-.84 1.875-1.875 1.875h-.75A1.875 1.875 0 013 19.875v-6.75z" />
    </svg>
  );

  if (!chartData) return null;

  return (
    <div className="space-y-2">
      <div className="flex justify-end items-center">
        <div className="flex space-x-2">
          <button
            onClick={() => setChartType('doughnut')}
            className={`p-1 rounded transition-colors ${
              chartType === 'doughnut'
                ? 'text-blue-500 bg-blue-100 dark:bg-blue-900 dark:text-blue-300'
                : 'text-gray-500 hover:text-blue-500 dark:text-gray-400 dark:hover:text-blue-300'
            }`}
            title="Afficher en camembert"
          >
            <DoughnutIcon />
          </button>
          <button
            onClick={() => setChartType('bar')}
            className={`p-1 rounded transition-colors ${
              chartType === 'bar'
                ? 'text-blue-500 bg-blue-100 dark:bg-blue-900 dark:text-blue-300'
                : 'text-gray-500 hover:text-blue-500 dark:text-gray-400 dark:hover:text-blue-300'
            }`}
            title="Afficher en barres horizontales"
          >
            <BarChartIcon />
          </button>
        </div>
      </div>
      <div className="h-[300px] relative">
        {chartType === 'doughnut' ? (
          <Doughnut ref={chartRef} data={chartData} options={chartOptions} />
        ) : (
          <Bar ref={chartRef} data={chartData} options={chartOptions} />
        )}
      </div>
    </div>
  );
};

export default DNAbsenceReasons;